import React from 'react';
import { motion } from 'framer-motion';
import { Check, X, ChevronRight, Battery, Brain, Bomb, ShieldAlert, Search, AlertTriangle, ScanLine } from 'lucide-react';
interface QuestionScreenProps {
  iconType: string;
  question: string;
  hint: string;
  onAnswer: (answer: 'yes' | 'no') => void;
  onSkip: () => void;
}
const getIcon = (type: string) => {
  const props = {
    size: 48,
    strokeWidth: 1.5,
    className: 'text-[#FF0000]'
  };
  switch (type) {
    case 'battery':
      return <Battery {...props} data-id="element-1321" />;
    case 'brain':
      return <Brain {...props} data-id="element-1322" />;
    case 'bomb':
      return <Bomb {...props} data-id="element-1323" />;
    case 'shield':
      return <ShieldAlert {...props} data-id="element-1324" />;
    case 'search':
      return <Search {...props} data-id="element-1325" />;
    case 'alert':
      return <AlertTriangle {...props} data-id="element-1326" />;
    default:
      return <AlertTriangle {...props} data-id="element-1327" />;
  }
};
export function QuestionScreen({
  iconType,
  question,
  hint,
  onAnswer,
  onSkip
}: QuestionScreenProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -20
  }} transition={{
    duration: 0.4,
    ease: [0.22, 1, 0.36, 1]
  }} className="relative z-10 w-full max-w-md mx-auto" data-id="element-1328">
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-[#FF0000]/5 rounded-full blur-[80px] pointer-events-none -z-10" data-id="element-1329" />

      <div className="text-center mb-12" data-id="element-1330">
        <motion.div className="relative inline-flex items-center justify-center w-32 h-32 mb-10" initial={{
        scale: 0.8,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        type: 'spring',
        stiffness: 200,
        damping: 20,
        delay: 0.1
      }} data-id="element-1331">
          {/* Rotating Rings */}
          <motion.div className="absolute inset-0 border border-dashed border-[#FF0000]/20 rounded-full" animate={{
          rotate: 360
        }} transition={{
          duration: 20,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-1332" />
          <motion.div className="absolute inset-4 border border-[#FF0000]/10 rounded-full" animate={{
          rotate: -360
        }} transition={{
          duration: 15,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-1333" />

          {/* Center Glow */}
          <div className="absolute inset-0 bg-[#FF0000]/5 blur-2xl rounded-full" data-id="element-1334" />

          {/* Icon Container */}
          <div className="relative z-10 bg-[#0a0a0a] w-20 h-20 rounded-2xl border border-[#FF0000]/20 flex items-center justify-center shadow-[0_0_30px_rgba(255,0,0,0.1)]" data-id="element-1335">
            <motion.div initial={{
            scale: 0.5,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            delay: 0.2,
            duration: 0.4
          }} data-id="element-1336">
              {getIcon(iconType)}
            </motion.div>
          </div>

          {/* Scanning Line */}
          <div className="absolute inset-0 overflow-hidden rounded-full opacity-30" data-id="element-1337">
            <motion.div className="w-full h-[2px] bg-[#FF0000] shadow-[0_0_10px_#FF0000]" animate={{
            top: ['0%', '100%', '0%']
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-1338" />
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2,
        duration: 0.4
      }} data-id="element-1339">
          <div className="inline-flex items-center gap-2 mb-6 px-3 py-1 border border-[#00FFFF]/20 bg-[#00FFFF]/5 rounded-full" data-id="element-1340">
            <span className="relative flex h-2 w-2" data-id="element-1341">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00FFFF] opacity-75" data-id="element-1342"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00FFFF]" data-id="element-1343"></span>
            </span>
            <span className="text-[10px] font-mono text-[#00FFFF] tracking-widest uppercase font-medium" data-id="element-1344">
              Pattern Detection Active
            </span>
          </div>

          <h2 className="text-3xl font-bold mb-4 leading-tight text-white tracking-tight" data-id="element-1345">
            {question}
          </h2>
        </motion.div>

        <motion.div className="relative inline-block max-w-sm mx-auto w-full" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.3,
        duration: 0.4
      }} data-id="element-1346">
          <div className="bg-white/5 border border-white/10 rounded-lg p-4 backdrop-blur-sm" data-id="element-1347">
            <div className="flex items-start gap-3" data-id="element-1348">
              <ScanLine size={16} className="text-[#FF0000] mt-1 flex-shrink-0" data-id="element-1349" />
              <p className="text-sm text-gray-400 leading-relaxed font-mono text-left" data-id="element-1350">
                <span className="text-[#FF0000] font-bold mr-2 tracking-wider text-xs uppercase" data-id="element-1351">
                  Analysis Hint:
                </span>
                {hint}
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="space-y-4 mb-8" data-id="element-1352">
        <motion.button onClick={() => onAnswer('yes')} className="group w-full relative overflow-hidden bg-[#FF0000]/10 hover:bg-[#FF0000] border border-[#FF0000]/50 hover:border-[#FF0000] text-[#FF0000] hover:text-white py-5 px-6 transition-all duration-300 rounded-xl" initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.4,
        duration: 0.4
      }} whileHover={{
        scale: 1.02
      }} whileTap={{
        scale: 0.98
      }} data-id="element-1353">
          <div className="flex items-center justify-between relative z-10" data-id="element-1354">
            <span className="font-bold text-lg tracking-wide" data-id="element-1355">
              Yes, definitely
            </span>
            <div className="w-8 h-8 rounded-full bg-[#FF0000]/20 group-hover:bg-white/20 flex items-center justify-center transition-colors" data-id="element-1356">
              <Check size={18} strokeWidth={3} data-id="element-1357" />
            </div>
          </div>
        </motion.button>

        <motion.button onClick={() => onAnswer('no')} className="group w-full relative overflow-hidden bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 text-gray-300 hover:text-white py-5 px-6 transition-all duration-300 rounded-xl" initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.5,
        duration: 0.4
      }} whileHover={{
        scale: 1.02
      }} whileTap={{
        scale: 0.98
      }} data-id="element-1358">
          <div className="flex items-center justify-between relative z-10" data-id="element-1359">
            <span className="font-bold text-lg tracking-wide" data-id="element-1360">
              No, not really
            </span>
            <div className="w-8 h-8 rounded-full bg-white/10 group-hover:bg-white/20 flex items-center justify-center transition-colors" data-id="element-1361">
              <X size={18} strokeWidth={3} data-id="element-1362" />
            </div>
          </div>
        </motion.button>
      </div>

      <motion.button onClick={onSkip} className="w-full text-gray-500 text-xs font-mono uppercase tracking-widest hover:text-white transition-colors duration-200 flex items-center justify-center gap-2 group py-2" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.6,
      duration: 0.3
    }} data-id="element-1363">
        Skip Analysis Node
        <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" data-id="element-1364" />
      </motion.button>
    </motion.div>;
}